// Contrail V3.5.1 / V3.7.2 — password reset (and invitation) with a CODE instead of a one-time link.
// Why: Microsoft 365 / Outlook "Safe Links" opens every link in an email before the person does, which used up the
// one-time reset link ("Email link is invalid or has expired"). A code can't be used by a link scanner.
// Flow: email → we send a code → enter the code here → choose a new password (SetPassword).
import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { endSetPassword, startSetPassword } from "../lib/setpw";

const RESEND_S = 60;

export default function ResetPassword() {
  const nav = useNavigate();
  const q = new URLSearchParams(window.location.search);
  const invite = q.get("mode") === "invite";
  // "+" in an address arrives as a space when the email client doesn't encode it
  const qEmail = (q.get("email") ?? "").trim().replace(/ /g, "+");
  const [email, setEmail] = useState(qEmail);
  const [step, setStep] = useState<"email" | "code">(q.get("step") === "email" ? "email" : qEmail || invite ? "code" : "email");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  useEffect(() => { if (!wait) return; const t = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(t); }, [wait]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    if (!email.trim()) return setError("Enter your email address.");
    setBusy(true); setError(null); setNote(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/reset` });
    setBusy(false);
    if (error && /rate|limit|seconds/i.test(error.message)) return setError("Please wait a minute before asking for another code.");
    if (error) return setError(error.message);
    // Same message whether or not the address exists, so accounts can't be discovered.
    setStep("code"); setCode(""); setWait(RESEND_S);
    setNote(`If an account exists for ${email.trim()}, we've emailed a code. It can take a few minutes and may land in junk or spam.`);
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    const token = code.replace(/\s|-/g, "");
    if (!/^\d{6,10}$/.test(token)) return setError("Enter the code from the email (numbers only).");
    setBusy(true); setError(null);
    startSetPassword(invite ? "invite" : "reset");   // before verifying, so the sign-in doesn't flash the map
    // V3.7.2: an invitation can carry an invite code (not yet accepted) or a sign-up code (account made with a temporary
    // password, sent as a recovery code), so try both — invite page first, reset page the other way round.
    const types = invite ? (["invite", "recovery"] as const) : (["recovery", "invite"] as const);
    let error: { message: string } | null = null;
    for (const type of types) {
      ({ error } = await supabase.auth.verifyOtp({ email: email.trim(), token, type }));
      if (!error || !/expired|invalid|not found/i.test(error.message)) break;
    }
    setBusy(false);
    if (error) {
      endSetPassword();
      return setError(/expired|invalid|not found/i.test(error.message)
        ? "That code is wrong or has expired. Check the latest email, or send a new code."
        : error.message);
    }
    nav("/set-password", { replace: true });
  }

  return (
    <div className="login">
      {step === "email" ? (
        <form className="login-card" onSubmit={send}>
          <img src="/logo.png" alt="Tracking Fiji" className="login-logo" />
          <h1>Reset your password</h1>
          <p className="muted">Enter your email and we'll send you a code.</p>
          <label>Email<input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></label>
          {error && <div className="error">{error}</div>}
          <button className="btn-primary" disabled={busy}>{busy ? "Sending…" : "Send code"}</button>
          <button type="button" className="linkish" onClick={() => { setError(null); setStep("code"); }}>I already have a code</button>
          <button type="button" className="linkish" onClick={() => nav("/login")}>Back to sign in</button>
        </form>
      ) : (
        <form className="login-card" onSubmit={verify}>
          <img src="/logo.png" alt="Tracking Fiji" className="login-logo" />
          <h1>{invite ? "Accept your invitation" : "Enter your code"}</h1>
          <p className="muted">{note ?? (invite ? "Enter the code from your invitation email, then choose a password." : "Enter the code from the email we sent you.")}</p>
          <label>Email<input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
          <label>Code<input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 -]*" maxLength={13}
            value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" required autoFocus /></label>
          {error && <div className="error">{error}</div>}
          <button className="btn-primary" disabled={busy}>{busy ? "Checking…" : "Continue"}</button>
          {!invite && <button type="button" className="linkish" disabled={busy || wait > 0} onClick={() => send()}>
            {wait > 0 ? `Send a new code (${wait}s)` : "Send a new code"}</button>}
          {invite && <p className="fine">Code expired? Ask your administrator to re-send the invitation, or use “Forgot password” on the sign-in page.</p>}
          <button type="button" className="linkish" onClick={() => nav("/login")}>Back to sign in</button>
        </form>
      )}
    </div>
  );
}
